/**
 * Sayfa Geçiş Animasyonu — perde + harf harf açılan kelime işareti
 *
 * Akış (flash'sız):
 *  1. 'covering'  — koyu yeşil perde aşağıdan yukarı ESKİ sayfayı örter
 *  2. perde oturunca → scroll top + displayLocation YENİ sayfaya geçer (perde altında)
 *     ve kelime işareti harf harf açılır:
 *       · HIRANOVA WORLD → harfler aşağıdan (+110%)
 *       · FAMILY CLUB    → harfler yukarıdan (−110%), yarım adım gecikmeli
 *     Harfler satır kutusunda kırpılır (overflow: clip) — fade yok, maskeleme var.
 *  3. 'revealing' — perde yukarı çekilir, yeni sayfa görünür (yazı perdeyle birlikte gider)
 */
import { useEffect, useRef, useState } from 'react'
import { useLocation, Routes } from 'react-router-dom'
import './PageTransition.css'

const COVER_MS = 460   // perde kapanır
const REVEAL_MS = 560  // perde çekilir

const LINE_1 = 'HIRANOVA WORLD'
const LINE_2 = 'FAMILY CLUB'

// Harf animasyonu süreleri — PageTransition.css ile senkron tutulmalı.
// Perde, TÜM harfler yerine oturup metin okunduktan sonra açılır (owner).
const L1_STAGGER = 30, L1_DUR = 530
const L2_STAGGER = 25, L2_DELAY = 130, L2_DUR = 465
const READ_MS = 120    // giriş ile çıkış arasında ölü (düz yeşil) bekleme olmasın
// Harf animasyonunun tam bitişi (giriş)
const ANIM_MS = Math.max(
  (LINE_1.length - 1) * L1_STAGGER + L1_DUR,
  (LINE_2.length - 1) * L2_STAGGER + L2_DELAY + L2_DUR,
)
// Çıkış: giriş etkisinin tam tersi, daha hızlı (CSS .is-out ile senkron)
const OUT_STAGGER = 17, OUT_DUR = 370
const OUT_MS = (Math.max(LINE_1.length, LINE_2.length) - 1) * OUT_STAGGER + OUT_DUR

type Stage = 'idle' | 'covering' | 'revealing'

interface Props {
  children: React.ReactNode  // <Routes> içeriği
}

/** Metni harflere böler; boşluk kırılmasın diye NBSP kullanılır. */
const chars = (text: string) =>
  text.split('').map((c, i) => (
    <span key={i} className="pt-char" style={{ '--i': i } as React.CSSProperties}>
      {c === ' ' ? ' ' : c}
    </span>
  ))

export default function PageTransition({ children }: Props) {
  const location = useLocation()
  const [displayLocation, setDisplayLocation] = useState(location)
  const [stage, setStage] = useState<Stage>('idle')
  const [textPhase, setTextPhase] = useState<'hidden' | 'in' | 'out'>('hidden')
  const timers = useRef<number[]>([])

  useEffect(() => {
    // location.key her gezinmede (aynı path olsa bile) değişir → her geçişte oynat
    if (location.key === displayLocation.key) return

    const reduce = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // Reduced motion: animasyon yok, sadece scroll + swap
    if (reduce) {
      window.scrollTo(0, 0)
      setDisplayLocation(location)
      return
    }

    timers.current.forEach(clearTimeout)
    timers.current = []

    setStage('covering')
    setTextPhase('hidden')

    // Sıralama kritik (owner: animasyon önceliği): harf animasyonu, yeni sayfanın
    // render'ıyla AYNI anda başlarsa ağır mount ana thread'i bloke eder ve
    // gecikmeler arka planda akar — harfler animasyonsuz "oturmuş" belirir.
    // Bu yüzden önce harfler eski sayfanın üstünde oynar (thread boş), yeni
    // sayfa ancak animasyon bittikten sonra, okuma payı sırasında yüklenir.
    const t1 = window.setTimeout(() => {
      setTextPhase('in')                // harfler açılsın (eski sayfa hâlâ altta)
      const t2 = window.setTimeout(() => {
        window.scrollTo(0, 0)
        setDisplayLocation(location)    // yeni sayfayı perde altında yükle (okuma payında)
        const t3 = window.setTimeout(() => {
          setTextPhase('out')           // harfler ters etkiyle kaybolur
          const t4 = window.setTimeout(() => {
            setStage('revealing')       // yazı bitti — perde çekilir
            const t5 = window.setTimeout(() => {
              setStage('idle')
              setTextPhase('hidden')
            }, REVEAL_MS)
            timers.current.push(t5)
          }, OUT_MS)
          timers.current.push(t4)
        }, READ_MS)
        timers.current.push(t3)
      }, ANIM_MS)
      timers.current.push(t2)
    }, COVER_MS)
    timers.current.push(t1)

    // Emniyet: herhangi bir sebeple (yavaş chunk, suspend/remount yarışı) zincir
    // kopsa bile perde kilitli kalmasın — normal akış + 1.5s sonra kesin açılır.
    const failsafe = window.setTimeout(() => {
      setDisplayLocation(location)
      setStage('idle')
      setTextPhase('hidden')
    }, COVER_MS + ANIM_MS + READ_MS + OUT_MS + REVEAL_MS + 1500)
    timers.current.push(failsafe)

    // Sekme gizliyken tarayıcı zincirli timer'ları dondurur (intensive throttling);
    // kullanıcı geri döndüğünde perde asılı kalmasın diye anında tamamla.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      timers.current.forEach(clearTimeout)
      timers.current = []
      setDisplayLocation(location)
      setStage('idle')
      setTextPhase('hidden')
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      timers.current.forEach(clearTimeout)
      timers.current = []
      document.removeEventListener('visibilitychange', onVisible)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key])

  return (
    <>
      <Routes location={displayLocation}>{children}</Routes>

      <div className={`page-transition pt-${stage}`} aria-hidden="true">
        <div className="pt-curtain">
          <div className={`pt-word${textPhase !== 'hidden' ? ` is-${textPhase}` : ''}`}>
            <span className="pt-line pt-line-1">{chars(LINE_1)}</span>
            <span className="pt-line pt-line-2">{chars(LINE_2)}</span>
          </div>
        </div>
      </div>
    </>
  )
}
